/**
 * `useBulkUnapprove` mutation hook.
 *
 * Wraps the raw `bulkUnapprove` service fn. Unapproving steps each
 * asset back one step (resetting its most recently approved step and everything
 * after it), so like `useBulkApprove` we invalidate the whole asset-approval-chain
 * namespace (open detail/timeline refetch), the folder files query, and the
 * folder list/detail aggregates.
 *
 * The `results` array is returned to the caller so the confirmation dialog can
 * report a per-asset success/failure summary. Caller owns error UX.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ASSET_APPROVAL_CHAIN_QUERY_KEYS } from '@/entities/asset-approval-chain';
import { PROJECT_FOLDER_QUERY_KEYS } from '@/entities/project-folder';
import { track } from '@/shared/analytics';

import { bulkUnapprove } from './bulk-approval.mutations.api';
import { invalidateFolderAggregates } from './invalidate-folder-aggregates';

export { bulkUnapprove };

interface UseBulkUnapproveOptions {
  projectId: string;
  /** Folder currently in view — used to refresh its files list after unapproving. */
  folderId?: string;
}

export function useBulkUnapprove(options: UseBulkUnapproveOptions) {
  const queryClient = useQueryClient();
  const { projectId, folderId } = options;

  return useMutation({
    mutationFn: (assetIds: string[]) => bulkUnapprove(projectId, assetIds),
    onSuccess: (result) => {
      // Broad prefix invalidation — refresh any open asset-approval-chain query.
      void queryClient.invalidateQueries({
        queryKey: ASSET_APPROVAL_CHAIN_QUERY_KEYS.namespace(projectId),
      });
      if (folderId) {
        void queryClient.invalidateQueries({
          queryKey: PROJECT_FOLDER_QUERY_KEYS.files(projectId, folderId),
        });
      }
      invalidateFolderAggregates(queryClient, projectId, folderId);

      const unapprovedCount = result.results.filter((r) => r.success).length;
      track('assets_bulk_unapproved', {
        project_id: projectId,
        unapproved_count: unapprovedCount,
        skipped_count: result.results.length - unapprovedCount,
        total_count: result.results.length,
      });
    },
  });
}
